import React, { ReactNode } from 'react';
import styled from 'styled-components/macro';
import { rgba } from 'polished';
import { useLocation } from 'react-router-dom';

import {
  Chip,
  Collapse,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Icon } from 'react-feather';
import { NavLinkWithNoStyle } from '../NavLinkWithNoStyle';

const Item = styled(ListItemButton)<{ $collapsed?: boolean }>`
  outline: none;
  width: ${(props) => (props.$collapsed ? '44px' : '100%')};
  min-width: ${(props) => (props.$collapsed ? '44px' : 'auto')};
  min-height: ${(props) => (props.$collapsed ? '40px' : '34px')};
  height: ${(props) => (props.$collapsed ? '40px' : 'auto')};
  margin: ${(props) => (props.$collapsed ? '0 auto 6px' : '0')};
  padding: ${(props) =>
    props.$collapsed
      ? '0'
      : `${props.theme.spacing(0.75)} ${props.theme.spacing(1.75)}`};
  justify-content: ${(props) => (props.$collapsed ? 'center' : 'flex-start')};
  border-radius: 8px;
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  svg {
    color: #cbd5e1;
    font-size: 18px;
    width: 18px;
    height: 18px;
    opacity: 1;
    flex-shrink: 0;
  }
  &:hover {
    background: #1e293b !important;
  }
  &.active,
  &.active:hover {
    background: #4338ca !important;
    box-shadow: 0 2px 5px rgba(15, 23, 42, 0.2);

    svg {
      color: #ffffff;
    }
  }
`;

const Title = styled(ListItemText)`
  margin: 0;
  span {
    color: #cbd5e1;
    font-size: 14px;
    line-height: 18px;
    padding: 0 ${(props) => props.theme.spacing(1.75)};
  }

  .active & span {
    color: #ffffff;
    font-weight: 500;
  }
`;

const Badge = styled(Chip)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 26px;
  top: 12px;
  background: ${(props) => props.theme.sidebar.badge.background};
  z-index: 1;
  span.MuiChip-label,
  span.MuiChip-label:hover {
    font-size: 11px;
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const SidebarNavListItem = (props: {
  title: string;
  href: string;
  children?: ReactNode;
  icon: Icon;
  badge: string;
  open?: boolean;
  collapsed?: boolean;
}) => {
  const {
    title,
    href,
    children,
    icon: Icon,
    badge,
    open: openProp = false,
    collapsed = false,
  } = props;

  const [open, setOpen] = React.useState(openProp);
  const location = useLocation();

  const handleToggle = () => {
    setOpen((state) => !state);
  };

  const isActive =
    location.pathname === href ||
    (href === '/presentations' &&
      location.pathname.startsWith('/presentations-editor/')) ||
    (href === '/devices' && location.pathname.startsWith('/device/'));

  if (children) {
    const item = (
      <Item
        $collapsed={collapsed}
        className={isActive ? 'active' : ''}
        onClick={handleToggle}
        aria-label={collapsed ? title : undefined}
      >
        {Icon && <Icon />}
        {!collapsed && (
          <Title>
            {title}
            {badge && <Badge label={badge} />}
          </Title>
        )}
        {!collapsed && (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
      </Item>
    );

    return (
      <React.Fragment>
        {collapsed ? (
          <Tooltip title={title} placement="right" arrow>
            {item}
          </Tooltip>
        ) : (
          item
        )}
        {!collapsed && <Collapse in={open}>{children}</Collapse>}
      </React.Fragment>
    );
  }

  const item = (
    <Item
      $collapsed={collapsed}
      className={isActive ? 'active' : ''}
      aria-label={collapsed ? title : undefined}
    >
      {Icon && <Icon />}
      {!collapsed && (
        <Title>
          {title}
          {badge && <Badge label={badge} />}
        </Title>
      )}
    </Item>
  );

  return (
    <React.Fragment>
      <NavLinkWithNoStyle to={href}>
        {collapsed ? (
          <Tooltip title={title} placement="right" arrow>
            {item}
          </Tooltip>
        ) : (
          item
        )}
      </NavLinkWithNoStyle>
    </React.Fragment>
  );
};

export default SidebarNavListItem;
